import { SyntheticEvent, useEffect, useState, useCallback, memo } from "react";
import { useIntl } from "react-intl";
import { DropdownProps, InputOnChangeData, TabProps } from "semantic-ui-react";

// Commons
import SnackMessage from "../common/SnackMessage";

// Components
import CardMeFormList from "./CardMeFormList";
import CardMeFormDetail from "./CardMeFormDetail";
import ModMeForm from "./ModMeForm";
import ModAddMeFormContent from "./ModAddMeFormContent";
import ModInReview from "./ModInReview";
import ModBuyBillingPackage from "../MEH/ModBuyBillingPackage";
import CardBroadcastEditor from "./CardBroadcastEditor";

// Interfaces
import { createMeFormBroadcastAdapter } from "./BroadcastAdapter";

// Types
import MainHISController from "@/HIS/MainHISController";
import {
  RunSequence as MeFormRunSequence,
  SetProp,
  State,
  PickedState,
  CreatedData,
  MAIN_CARD_NAME,
  FlatNode,
  TreeNode,
  ALL_BROADCAST_STATUSES,
} from "./MeFormType";

import { BroadcastMode, RunSequence as MeCastRunSequence } from "./MeCastType";

// Serializers
import DiagRuleSerializer from "@/issara-sdk/types/DiagRuleSerializer_apps_PRX";
import PublishedDiagRuleNodeSerializer from "@/issara-sdk/types/PublishedDiagRuleNodeSerializer_apps_PRX";

// Const
import { BROADCAST_HEADER } from "./sequence/MeCast";

type CardMeFormMainProps = {
  runSequence: MeFormRunSequence & MeCastRunSequence;
  setProp: SetProp;
  MeFormSequence: State["MeFormSequence"];
  selectedDivisionId: number;

  // Change Tab
  onGotoMeCast: () => void;

  // IsChart
  controller: MainHISController;
  loggedin?: boolean;
  userProfile?: Record<string, any>;
  selectedDivision: any;

  // Buy Credit
  BillingAndPackageSequence?: any;
  BillingCarts?: any;
  onHistoryClick?: () => void;
  onEvent?: any;
  onProceed?: () => void;
  termsAndConditionsList?: any;

  languageUX: "en" | "th";
} & Pick<
  PickedState,
  "buttonLoadCheck" | "errorMessage" | "successMessage" | "loadingStatus" | "BillingCreditWallet"
>;

type BroadcastScreenState = {
  mode: BroadcastMode;
  formId: number;
  node: FlatNode | TreeNode;
  initPdrNode?: PublishedDiagRuleNodeSerializer | null;
};

type ScreenState =
  | { screen: "LIST" }
  | { screen: "DETAIL"; formId: number }
  | { screen: "BROADCAST"; data: BroadcastScreenState };

const defaultCreateData: CreatedData = {
  form_type: "DECISION_TREE",
};

const CardMeFormMain = (props: CardMeFormMainProps) => {
  const intl = useIntl();

  const [screen, setScreen] = useState<ScreenState>({ screen: "LIST" });
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [openAddForm, setOpenAddForm] = useState<boolean>(false);
  const [createdData, setCreatedData] = useState<CreatedData>(defaultCreateData);
  const [openEditMod, setOpenEditMod] = useState<boolean>(false);
  const [editedData, setEditedData] = useState<CreatedData>(null);
  const [openDuplicateMod, setOpenDuplicateMod] = useState<boolean>(false);
  const [duplicatedData, setDuplicatedData] = useState<CreatedData>(null);
  const [openPublishMod, setOpenPublishMod] = useState<boolean>(false);
  const [publishTargetForm, setPublishTargetForm] = useState<DiagRuleSerializer | null>(null);
  const [broadcastNode, setBroadcastNode] = useState<FlatNode | TreeNode | null>(null);
  const [selectedDetailNode, setSelectedDetailNode] = useState<TreeNode | null>(null);
  const [selectedPdrNode, setSelectedPdrNode] = useState<PublishedDiagRuleNodeSerializer | null>(
    null,
  );
  const [headerOnCreateBroadcast, setHeaderOnCreateBroadcast] = useState<string>(
    BROADCAST_HEADER.CREATE,
  );
  const [openBuyCredit, setOpenBuyCredit] = useState<boolean>(false);
  const [openInReviewPopup, setOpenInReviewPopup] = useState<boolean>(false);

  useEffect(() => {
    props.runSequence({
      sequence: "MeForm",
      restart: true,
    });

    setScreen({ screen: "LIST" });
  }, [props.selectedDivision]); // reset when change division use "props.selectedDivisionId?.id" dependency

  useEffect(() => {
    const formId = props.MeFormSequence?.formDetailPage?.formId;

    if (!formId) {
      setScreen({ screen: "LIST" });
      return;
    }

    setScreen({ screen: "DETAIL", formId });
  }, [props.MeFormSequence?.formDetailPage?.formId]);

  useEffect(() => {
    const pdrList = props.MeFormSequence?.formDetailPage?.pdrNodeList || [];
    const pdrNode = pdrList.find((pdr) => pdr.node_id === broadcastNode?.id) ?? null;

    setSelectedPdrNode(pdrNode);
  }, [props.MeFormSequence?.formDetailPage?.pdrNodeList, broadcastNode]);

  useEffect(() => {
    if (props.MeFormSequence?.formDetailPage?.broadcastDetail) {
      setHeaderOnCreateBroadcast(BROADCAST_HEADER.EDIT);
    }
  }, [props.MeFormSequence?.formDetailPage]);

  useEffect(() => {
    if (props.MeFormSequence?.formDetailPage?.broadcastDetail?.status === "PENDING_REVIEW") {
      setOpenInReviewPopup(true);
    }
  }, [props.MeFormSequence?.formDetailPage?.broadcastDetail]);

  const handleAddForm = () => {
    setCreatedData(null);
    setOpenAddForm(true);
  };

  const handleEditForm = (formDetail?: DiagRuleSerializer) => {
    if (!formDetail) return;

    setEditedData({
      name: formDetail?.name || "",
      form_type: formDetail?.form_type,
      category_name_list: formDetail?.category_name_list,
    });

    setOpenEditMod(true);
  };

  const handleDuplicateForm = (formDetail?: DiagRuleSerializer) => {
    if (!formDetail) return;

    setDuplicatedData({
      name: `Copy of ${formDetail.name}`,
      form_type: formDetail.form_type,
      category_name_list: formDetail.category_name_list,
      content: formDetail.content,
      published: false,
    });

    setOpenDuplicateMod(true);
  };

  // const handleDuplicateForm = useCallback((formDetail?: DiagRuleSerializer) => {
  //     if (!formDetail) return;

  //     const duplicatedData: CreatedData = {
  //       name: `Copy of ${formDetail.name}`,
  //       form_type: formDetail.form_type,
  //       category_name_list: formDetail.category_name_list,
  //       content: formDetail.content,
  //       published: false,
  //     };

  //     props.runSequence({
  //       sequence: "MeForm",
  //       action: "ADD_FORM",
  //       createdData: duplicatedData,
  //       division: props.selectedDivisionId,
  //     });
  //   },
  //   [props.runSequence, props.selectedDivisionId]
  // );

  const handlePublishForm = (formDetail?: DiagRuleSerializer) => {
    if (!formDetail?.id) return;

    setPublishTargetForm(formDetail);
    setOpenPublishMod(true);
  };

  const handleConfirmPublish = () => {
    if (!publishTargetForm || !publishTargetForm?.id) return;

    props.runSequence({
      sequence: "MeForm",
      action: "PUBLISH_FORM",
      data: { content: publishTargetForm.content },
      formId: publishTargetForm.id,
      division: props.selectedDivisionId,
    });

    setOpenPublishMod(false);
    setPublishTargetForm(null);
  };

  const handleClosePublishMod = () => {
    setOpenPublishMod(false);
    setPublishTargetForm(null);
  };

  const handleCloseAddFormMod = () => {
    setCreatedData(null);
    setOpenAddForm(false);
  };

  const handleCloseEditMod = () => {
    setEditedData(null);
    setOpenEditMod(false);
  };

  const handleCloseDuplicateMod = () => {
    setDuplicatedData(null);
    setOpenDuplicateMod(false);
  };

  const handleCheckCreateRequired = (data: CreatedData): boolean => {
    if (!data?.name?.trim()) {
      props.setProp(`errorMessage.${MAIN_CARD_NAME}`, "Form name cannot empty!");
      return false;
    }

    if (!data.form_type) {
      props.setProp(`errorMessage.${MAIN_CARD_NAME}`, "Form Type cannot empty!");
      return false;
    }

    if (!data?.category_name_list || data.category_name_list.length === 0) {
      props.setProp(`errorMessage.${MAIN_CARD_NAME}`, "Form Category cannot empty!");
      return false;
    }

    return true;
  };

  const handleConfirmAddForm = useCallback(() => {
    if (!handleCheckCreateRequired(createdData)) return;

    setOpenAddForm(false);

    props.runSequence({
      sequence: "MeForm",
      action: "ADD_FORM",
      createdData,
      division: props.selectedDivisionId,
      onSuccess: handleCloseAddFormMod,
    });
  }, [props.runSequence, props.selectedDivisionId, createdData]);

  const handleConfirmEditForm = useCallback(() => {
    if (!editedData) return;
    if (!handleCheckCreateRequired(editedData)) return;

    const formId = props.MeFormSequence?.formDetailPage?.formId || 0;

    props.runSequence({
      sequence: "MeForm",
      action: "EDIT_FORM",
      editedData,
      formId,
      division: props.selectedDivisionId,
      onSuccess: handleCloseAddFormMod,
    });

    setOpenEditMod(false);
  }, [props.runSequence, props.selectedDivisionId, editedData]);

  const handleConfirmDuplicateForm = useCallback(() => {
    if (!duplicatedData) return;
    if (!handleCheckCreateRequired(duplicatedData)) return;

    props.runSequence({
      sequence: "MeForm",
      action: "ADD_FORM",
      createdData: duplicatedData,
      division: props.selectedDivisionId,
    });

    setOpenDuplicateMod(false);
  }, [props.runSequence, props.selectedDivisionId, duplicatedData]);

  const handleSelectForm = (id: number) => {
    setScreen({ screen: "DETAIL", formId: id });
  };

  const handleCloseSnackMessage = () => {
    props.setProp(`errorMessage.${MAIN_CARD_NAME}`, null);
    props.setProp(`successMessage.${MAIN_CARD_NAME}`, null);
  };

  const handleChangeCreatedData = (e: SyntheticEvent, data: DropdownProps | InputOnChangeData) => {
    setCreatedData((prev) => ({
      ...prev,
      [data.name as keyof CreatedData]: data.value,
    }));
  };

  const handleChangeEditedData = (e: SyntheticEvent, data: DropdownProps | InputOnChangeData) => {
    console.log("Debug ~ handleChangeEditedData ~ data:", data)
    
    setEditedData((prev) => ({
      ...prev,
      [data.name as keyof CreatedData]: data.value,
    }));
  };

  const handleChangeDuplicatedData = (
    e: SyntheticEvent,
    data: DropdownProps | InputOnChangeData,
  ) => {
    setDuplicatedData((prev) => ({
      ...prev,
      [data.name as keyof CreatedData]: data.value,
    }));
  };

  const handleOpenPost = (node: FlatNode | TreeNode | null) => {
    if (!node) return;

    const formId = props.MeFormSequence?.formDetailPage?.formId;

    if (!formId) return;

    const pdrList = props.MeFormSequence?.formDetailPage?.pdrNodeList || [];
    const pdrNode = pdrList.find((pdr) => pdr.node_id === node.id) ?? null;

    props.runSequence({
      sequence: "MeForm",
      action: "ADD_CONTENT",
    });

    setBroadcastNode(node);

    setScreen({
      screen: "BROADCAST",
      data: { mode: "CREATE", formId, node, initPdrNode: pdrNode },
    });
  };

  const handleGoBackToListPage = () => {
    props.runSequence({
      sequence: "MeForm",
      action: "FETCH",
    });

    setSelectedDetailNode(null); // clear select node
    setScreen({ screen: "LIST" });
  };

  const handleGoBackToDetailPage = () => {
    if (screen.screen !== "BROADCAST") return;

    const { formId, initPdrNode } = screen.data;

    if (!initPdrNode?.id) return;

    props.runSequence({
      sequence: "MeForm",
      action: "GET_BROADCAST_NODE_LIST",
      broadcastNodeParams: {
        published_diag_rule_node: initPdrNode.id,
        statuses: ALL_BROADCAST_STATUSES,
      },
    });

    setSelectedPdrNode(null);
    setBroadcastNode(null);
    setScreen({ screen: "DETAIL", formId });
  };

  const handleGotoMeCast = () => {
    props.setProp(`MeFormSequence.formDetailPage.broadcastDetail`, undefined);
    props.onGotoMeCast();
  };

  const handleChangeTab = (e: SyntheticEvent, data: TabProps) => {
    setActiveTabIndex(data.activeIndex as number);
  };

  const handlePageChange = (type: "active" | "inactive", activePage: number) => {
    props.runSequence({
      sequence: "MeForm",
      action: "FETCH",
      ...(type === "active" ? { activePage } : { inactivePage: activePage }),
    });
  };

  const handleOpenBuyCredit = () => setOpenBuyCredit(true);
  const handleCloseBuyCredit = () => setOpenBuyCredit(false);

  return (
    <div style={{ padding: "15px 0", height: "100%" }}>
      {/* --- ErrorMessage --- */}
      <SnackMessage
        error={props.errorMessage?.[MAIN_CARD_NAME]}
        success={props.successMessage?.[MAIN_CARD_NAME]} // *
        onClose={handleCloseSnackMessage}
      />

      {/* --- Mod In Review --- */}
      <ModInReview
        open={openInReviewPopup}
        onClose={() => setOpenInReviewPopup(false)}
        onBack={handleGotoMeCast}
        // data
        broadcast={props.MeFormSequence?.formDetailPage?.broadcastDetail}
        updatedByUserFirebase={props.MeFormSequence?.formDetailPage?.updatedByUserFirebase}
      />

      {/* --- Mod Buy Credit --- */}
      <ModBuyBillingPackage
        // func & callback
        open={openBuyCredit}
        onClose={handleCloseBuyCredit}
        // CardBuyBillingPackageProps
        BillingAndPackageSequence={props.BillingAndPackageSequence}
        BillingCreditWallet={props.BillingCreditWallet}
        BillingCarts={props.BillingCarts}
        onHistoryClick={props.onHistoryClick}
        controller={props.controller}
        runSequence={props.runSequence}
        onEvent={props.onEvent}
        setProp={props.setProp}
        onProceed={props.onProceed}
        termsAndConditionsList={props.termsAndConditionsList}
        languageUX={props.languageUX as any}
      />

      {screen.screen === "LIST" && (
        <CardMeFormList
          // main props
          runSequence={props.runSequence}
          setProp={props.setProp}
          MeFormSequence={props.MeFormSequence}
          selectedDivisionId={props.selectedDivisionId}
          languageUX={props.languageUX}
          // common
          buttonLoadCheck={props.buttonLoadCheck}
          loadingStatus={props.loadingStatus}
          // func
          activeTabIndex={activeTabIndex}
          onChangeTab={handleChangeTab}
          onAddForm={handleAddForm}
          onSelectForm={handleSelectForm}
          onPageChange={handlePageChange}
          // isChart
          controller={props.controller}
          loggedin={props.loggedin}
          userProfile={props.userProfile}
          selectedDivision={props.selectedDivision}
        />
      )}

      {screen.screen === "DETAIL" && (
        <CardMeFormDetail
          runSequence={props.runSequence}
          setProp={props.setProp}
          MeFormSequence={props.MeFormSequence}
          selectedDivisionId={props.selectedDivisionId}
          languageUX={props.languageUX}
          // common
          buttonLoadCheck={props.buttonLoadCheck}
          loadingStatus={props.loadingStatus}
          selectedDivision={props.selectedDivision}
          BillingCreditWallet={props.BillingCreditWallet}
          // data
          selectedNode={selectedDetailNode}
          // func
          onSelectNode={setSelectedDetailNode}
          onGoBack={handleGoBackToListPage}
          onEditForm={handleEditForm}
          onPublishForm={handlePublishForm}
          onOpenPostBroadcast={handleOpenPost}
          onDuplicateForm={handleDuplicateForm}
        />
      )}

      {screen.screen === "BROADCAST" && (
        <CardBroadcastEditor
          sequenceName="MeForm"
          mode={screen.data.mode}
          header={headerOnCreateBroadcast}
          context={{
            broadcast: props.MeFormSequence?.formDetailPage?.broadcastDetail,
            firebase: props.MeFormSequence?.firebaseData?.selectedBroadcast,

            // Form
            initPdrNode: screen.data.initPdrNode,
            patientByLeafList: props.MeFormSequence?.formDetailPage?.patientByLeafList,
            pdrNodeList: props.MeFormSequence?.formDetailPage?.pdrNodeList,

            // Infra
            postCalculateCredit: props.MeFormSequence?.formDetailPage?.postCalculateCredit,
            updatedByUserFirebase: props.MeFormSequence?.formDetailPage?.updatedByUserFirebase,
          }}
          actions={createMeFormBroadcastAdapter({
            mode: screen.data.mode,
            runSequence: props.runSequence,
            selectedBroadcast: props.MeFormSequence?.formDetailPage?.broadcastDetail,
            selectedFirebase: props.MeFormSequence?.firebaseData?.selectedBroadcast,
          })}
          onBack={handleGoBackToDetailPage}
          BillingCreditWallet={props.BillingCreditWallet}
          buttonLoadCheck={props.buttonLoadCheck}
          onOpenBuyCredit={handleOpenBuyCredit}
        />
      )}

      {/* --- Create Form Mod --- */}
      <ModMeForm
        // Func
        open={openAddForm}
        onClose={handleCloseAddFormMod}
        onConfirm={handleConfirmAddForm}
        // detail
        header="Create a new form"
        content={
          <ModAddMeFormContent
            createdData={createdData}
            formCategoryList={props.MeFormSequence?.common?.formCategoryList || []}
            onChangeData={handleChangeCreatedData}
          />
        }
      />

      {/* --- Edit Form Mod --- */}
      <ModMeForm
        // Func
        open={openEditMod}
        onClose={handleCloseEditMod}
        onConfirm={handleConfirmEditForm}
        // detail
        header="Edit form"
        content={
          <ModAddMeFormContent
            isEdit
            createdData={editedData}
            formCategoryList={props.MeFormSequence?.common?.formCategoryList || []}
            onChangeData={handleChangeEditedData}
          />
        }
      />

      {/* --- Duplicate Form Mod --- */}
      <ModMeForm
        // Func
        open={openDuplicateMod}
        onClose={handleCloseDuplicateMod}
        onConfirm={handleConfirmDuplicateForm}
        // detail
        header="Duplicate form"
        content={
          <ModAddMeFormContent
            isEdit
            createdData={duplicatedData}
            formCategoryList={props.MeFormSequence?.common?.formCategoryList || []}
            onChangeData={handleChangeDuplicatedData}
          />
        }
      />

      {/* --- Publish Mod --- */}
      <ModMeForm
        // Func
        open={openPublishMod}
        onClose={handleClosePublishMod}
        onConfirm={handleConfirmPublish}
        // detail
        header={intl.formatMessage({ id: "ยืนยันการเผยแพร่ฟอร์ม" })}
        content={
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              textAlign: "center",
              lineHeight: 2,
            }}
          >
            <label>
              {intl.formatMessage({
                id: "เมื่อเผยแพร่แล้ว ฟอร์มนี้จะเป็นสาธารณะและไม่สามารถแก้ไขข้อมูลได้ในภายหลัง คุณต้องการดำเนินการต่อหรือไม่?",
              })}
            </label>
          </div>
        }
      />
    </div>
  );
};

CardMeFormMain.displayName = "CardMeFormMain";

export default memo(CardMeFormMain);
